import React from 'react';
import Config from 'config';
import AppHeaderOptionsMenu from 'components/@layout/AppBar/AppHeaderOptionsMenu';
import useRouteAction from 'hooks/route-action/use-route-action';
import { makeStyles } from '@material-ui/core/styles';
import { ModalVariant } from 'providers/ModalProvider';
import useStore from 'store';
import EditOrganizationModal from './EditOrganizationModal';
import { ModuleProps, VoidCallback } from 'types';
import { Organization } from 'types/schema-generated-types';

const { privilegesGroups } = Config;

export enum OrganizationModalVariant {
  EDIT = 'EDIT'
}

export enum OrganizationModalRouteAction {
  EDIT_ORGANIZATION = 'edit-organization'
}

export interface MoreOptionsHandlers {
  openOrganizationModal: (modalVariant: OrganizationModalVariant) => VoidCallback;
  organizationId: number;
}

interface ModalHandlers {
  closeModal: VoidCallback;
}

const useStyles = makeStyles(() => ({
  moreOptionsIcon: {
    fontSize: '1.5rem',
    color: 'black'
  }
}));

const modalFactory = (
  modalVariant: OrganizationModalVariant,
  { closeModal: handleOnClose }: ModalHandlers
) =>
  ({
    [OrganizationModalVariant.EDIT]: {
      routeAction: OrganizationModalRouteAction.EDIT_ORGANIZATION,
      title: 'Edit Organization',
      component: <EditOrganizationModal onClose={handleOnClose} />,
      modalVariant: ModalVariant.QUARTER
    }
  }[modalVariant]);

interface ActionPanel {
  label: string;
  title: string;
  options: {
    label: string;
    onClick?: () => void;
    privileges?: string[];
  }[];
}

const OrganizationOptionsMenu: React.FC<ModuleProps & {
  organization: Organization;
}> = ({ organization }) => {
  const classes = useStyles();
  const [
    { userPrivilegesCache },
    { openFullModal, openHalfModal, openQuarterModal, closeModal }
  ] = useStore();

  const openOrganizationModal = (
    organizationModalVariant: OrganizationModalVariant
  ) => (): void => {
    const modal = modalFactory(organizationModalVariant, { closeModal });

    let openModal = openFullModal;
    if (modal.modalVariant === ModalVariant.QUARTER) openModal = openQuarterModal;
    if (modal.modalVariant === ModalVariant.HALF) openModal = openHalfModal;

    openModal({
      title: modal.title,
      body: modal.component,
      routeAction: modal.routeAction,
      routeParams: { organizationId: organization.id }
    });
  };

  const organizationOptions = [
    {
      label: 'organization-actions',
      options: [
        {
          label: 'Edit Organization Details',
          onClick: openOrganizationModal(OrganizationModalVariant.EDIT),
          privileges: privilegesGroups.mutateOrganization
        }
      ]
    }
  ];

  const options = [...organizationOptions].map(x => {
    return {
      ...x,
      options: (x.options as any).filter((y: any) =>
        userPrivilegesCache.isPrivileged(y.privileges ?? [], {
          organizationId: organization.id
        })
      )
    };
  });

  useRouteAction(({ action }) => {
    if (!action) return;
    switch (action) {
      case OrganizationModalRouteAction.EDIT_ORGANIZATION:
        return openOrganizationModal(OrganizationModalVariant.EDIT)();
      default:
        return null;
    }
  });

  return (
    <React.Fragment>
      <AppHeaderOptionsMenu
        buttonSize="small"
        id={'more-options-menu'}
        aria-label="More options menu."
        iconClassName={classes.moreOptionsIcon}
        optionSections={options}
      />
    </React.Fragment>
  );
};

export default OrganizationOptionsMenu;
